import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, useReducedMotion } from 'framer-motion';
import { Zap } from 'lucide-react';
import { speedStatView } from '@/lib/searchSpeedStat';

/**
 * Race-timing pill over the flight results (founder, 2026-10-22: "make it
 * look a bit cooler than Google's ... whiplash or speed/race design"; then
 * "less tall ... more pill like", so it wears the price pill's shape and
 * 34px height, AllInPriceToggle, with the time in a badge).
 *
 * Speed streaks whip across as it lands, and the stopwatch replays this
 * search's own time (press to first flights) and stops on it. The flight
 * count runs up live while the rest of the flights stream in, and a
 * checkered flag drops once they're all in. Fast searches only
 * (speedStatView); with reduced motion it simply shows the numbers.
 */
export default function SearchSpeedStat({ ms, count, complete, compact = false }) {
  const view = speedStatView(ms);
  const reduceMotion = useReducedMotion();
  const shown = useStopwatch(view?.seconds ?? null, !reduceMotion);
  if (!view || !count) return null;

  const stopped = view.instant || shown === view.seconds;
  const flights = count === 1 ? 'flight' : 'flights';
  return (
    <motion.div
      className={`relative inline-flex h-[34px] max-w-full items-center overflow-hidden rounded-full border border-white/10 bg-white/[0.04] pl-1.5 transition-shadow duration-500 ${compact ? 'gap-1.5 pr-2.5' : 'gap-2 pr-3'} ${stopped ? 'shadow-[0_0_18px_-6px_rgba(251,191,36,0.35)]' : ''}`}
      initial={reduceMotion ? false : { opacity: 0, x: -20, skewX: -14 }}
      animate={{ opacity: 1, x: 0, skewX: 0 }}
      transition={{ type: 'spring', stiffness: 560, damping: 24, mass: 0.7 }}
      data-testid="search-speed-stat"
    >
      <span className="sr-only">
        {view.instant ? 'Instant results.' : `First results in ${view.seconds} seconds.`} {count} {flights}.
      </span>

      {/* The whiplash: streaks race across once as it lands. */}
      {!reduceMotion && (
        <span aria-hidden="true" className="pointer-events-none absolute inset-0">
          {[34, 64].map((top, i) => (
            <span key={top} className="dfr-speed-streak" style={{ top: `${top}%`, animationDelay: `${i * 60}ms` }} />
          ))}
        </span>
      )}

      {/* The time, in the pill's badge. */}
      <span aria-hidden="true" className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-400/15 py-0.5 pl-1.5 pr-2">
        <Zap className="h-3 w-3 fill-amber-300 text-amber-300" strokeWidth={1.5} />
        {/* A shine runs through the digits the moment the clock stops. */}
        <span className={`dfr-speed-digits pr-0.5 text-[13px] font-medium italic leading-none tabular-nums ${stopped && !reduceMotion ? 'dfr-speed-shine' : ''}`}>
          {view.instant ? 'Instant' : (
            <>
              {shown}
              <span className="text-[11px]">s</span>
            </>
          )}
        </span>
      </span>

      {/* Compact (phones, beside the price pill): the time and the count only. */}
      {!compact && (
        <>
          <span aria-hidden="true" className="whitespace-nowrap text-[13px] font-normal leading-none text-white/60">{view.instant ? 'results' : 'to results'}</span>
          <span aria-hidden="true" className="h-3.5 w-px shrink-0 rotate-[20deg] bg-white/15" />
        </>
      )}

      <span aria-hidden="true" className={`flex items-center whitespace-nowrap text-[13px] leading-none ${compact ? 'gap-1' : 'gap-1.5'}`}>
        {complete ? <CheckeredFlag wave={!reduceMotion} /> : <LiveDot />}
        <motion.span
          key={count}
          className="font-medium text-white/85 tabular-nums"
          initial={reduceMotion ? false : { y: -4, opacity: 0.35 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.18 }}
        >
          {count}
        </motion.span>
        <span className="font-normal text-white/60">{flights}</span>
      </span>

      <style>{`
        @keyframes dfr-speed-streak {
          0% { transform: translateX(-130%) scaleX(0.35); opacity: 0; }
          18% { opacity: 1; }
          100% { transform: translateX(460%) scaleX(1); opacity: 0; }
        }
        .dfr-speed-streak {
          position: absolute; left: 0; height: 1px; width: 26%; border-radius: 999px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.85) 55%, rgba(253,230,138,0.95));
          animation: dfr-speed-streak 560ms cubic-bezier(0.2, 0.75, 0.2, 1) both;
        }
        .dfr-speed-digits {
          background-image: linear-gradient(100deg, #fde68a 0%, #fde68a 42%, #ffffff 50%, #fde68a 58%, #fde68a 100%);
          background-size: 300% 100%; background-position: 100% 0;
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        @keyframes dfr-speed-shine { from { background-position: 100% 0; } to { background-position: 0% 0; } }
        .dfr-speed-shine { animation: dfr-speed-shine 800ms ease-out both; }
        @keyframes dfr-flag-drop {
          0% { transform: rotate(-28deg) translateY(-4px); opacity: 0; }
          60% { transform: rotate(9deg); opacity: 1; }
          100% { transform: rotate(0deg); opacity: 1; }
        }
        .dfr-flag-drop { transform-origin: 0% 100%; animation: dfr-flag-drop 420ms ease-out both; }
      `}</style>
    </motion.div>
  );
}

SearchSpeedStat.propTypes = {
  /** Press-to-first-flights time for this search (ms_first). */
  ms: PropTypes.number,
  /** Flights found so far; runs up as the rest stream in. */
  count: PropTypes.number,
  /** Every flight is in (the checkered flag). */
  complete: PropTypes.bool,
  /** Phones, sharing a line with the price pill: drops "to results". */
  compact: PropTypes.bool,
};

/** Counts up to `target` ("0.42") over the search's own time, capped, then stops on it. */
function useStopwatch(target, animate) {
  const [shown, setShown] = useState(animate ? '0.00' : target);
  useEffect(() => {
    if (target == null || !animate || typeof window.requestAnimationFrame !== 'function') {
      setShown(target);
      return undefined;
    }
    const seconds = Number(target);
    const duration = Math.min(700, Math.max(250, seconds * 1000));
    const start = performance.now();
    let frame = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      if (t >= 1) {
        setShown(target);
        return;
      }
      setShown((seconds * (1 - (1 - t) ** 3)).toFixed(2));
      frame = window.requestAnimationFrame(tick);
    };
    setShown('0.00');
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [target, animate]);
  return shown;
}

function LiveDot() {
  return (
    <span className="relative flex h-1.5 w-1.5">
      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400/70 motion-safe:animate-ping" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
    </span>
  );
}

function CheckeredFlag({ wave }) {
  return (
    <span className={`relative inline-block h-[12px] w-[13px] ${wave ? 'dfr-flag-drop' : ''}`} data-testid="speed-stat-flag">
      <span className="absolute bottom-0 left-0 top-0 w-px rounded-full bg-white/75" />
      <span
        className="absolute left-px top-0 h-[8px] w-[11px] rounded-[1px]"
        style={{ background: 'repeating-conic-gradient(#ffffff 0% 25%, #111827 0% 50%) 0 0 / 5.5px 5.5px' }}
      />
    </span>
  );
}

CheckeredFlag.propTypes = { wave: PropTypes.bool };
